import type {
    BillingCycle,
    Customer,
    CustomerStatus,
    PaymentMethod,
    PlanId
} from '@/types'
import type { Random } from '@/lib/random'

import { createRandom } from '@/lib/random'
import { TODAY, addDays, daysBetween } from '@/lib/date'
import { PLANS, computeMrr } from '@/data/plans'
import { CITIES, getProvince } from '@/constants/dashboard'

// Sample customer base for the design. Fourteen hand written accounts are used by the
// activity feed and notifications; the rest are generated from a fixed seed so every
// page shows the same numbers on every load.

type CustomerSeed = {
    company: string
    contact: string
    email: string
    plan: PlanId
    status: CustomerStatus
    cycle: BillingCycle
    seats: number
    city: string
    joinedAt: string
    canceledAt?: string
    health: number
    paymentMethod: PaymentMethod
}

const FEATURED: CustomerSeed[] = [
    { company: 'Kopi Senja Nusantara', contact: 'Dimas Prasetyo', email: 'dimas@kopisenja.id', plan: 'scale', status: 'active', cycle: 'monthly', seats: 24, city: 'Bandung', joinedAt: '2024-03-12', health: 92, paymentMethod: 'card' },
    { company: 'PT Lintas Data Mandiri', contact: 'Sari Wulandari', email: 'sari.wulandari@lintasdata.co.id', plan: 'enterprise', status: 'active', cycle: 'annual', seats: 180, city: 'Jakarta Selatan', joinedAt: '2023-08-21', health: 88, paymentMethod: 'transfer' },
    { company: 'Batik Kirana Studio', contact: 'Ayu Lestari', email: 'ayu@batikkirana.com', plan: 'starter', status: 'active', cycle: 'monthly', seats: 2, city: 'Yogyakarta', joinedAt: '2025-06-02', health: 81, paymentMethod: 'qris' },
    { company: 'Klinik Sehat Sentosa', contact: 'dr. Bima Santoso', email: 'bima@kliniksentosa.id', plan: 'scale', status: 'active', cycle: 'annual', seats: 32, city: 'Surabaya', joinedAt: '2024-01-15', health: 90, paymentMethod: 'va' },
    { company: 'CV Maju Bersama Logistik', contact: 'Hendra Gunawan', email: 'hendra@majubersama.co.id', plan: 'growth', status: 'at-risk', cycle: 'monthly', seats: 9, city: 'Medan', joinedAt: '2024-09-03', health: 31, paymentMethod: 'va' },
    { company: 'Rumah Desain Arsa', contact: 'Nadia Putri', email: 'nadia@arsadesain.com', plan: 'growth', status: 'active', cycle: 'monthly', seats: 6, city: 'Denpasar', joinedAt: '2025-02-18', health: 84, paymentMethod: 'card' },
    { company: 'Warung Tani Digital', contact: 'Agus Salim', email: 'agus@warungtani.id', plan: 'starter', status: 'canceled', cycle: 'monthly', seats: 1, city: 'Malang', joinedAt: '2025-04-07', canceledAt: '2026-11-02', health: 12, paymentMethod: 'ewallet' },
    { company: 'PT Samudra Niaga Utama', contact: 'Lestari Handayani', email: 'lestari@samudraniaga.co.id', plan: 'enterprise', status: 'active', cycle: 'monthly', seats: 260, city: 'Jakarta Utara', joinedAt: '2023-05-09', health: 95, paymentMethod: 'transfer' },
    { company: 'Kafe Langit Biru', contact: 'Kevin Wijaya', email: 'kevin@langitbiru.id', plan: 'growth', status: 'trial', cycle: 'monthly', seats: 4, city: 'Jakarta Barat', joinedAt: '2026-11-03', health: 64, paymentMethod: 'qris' },
    { company: 'Klinik Gigi Senyum Sehat', contact: 'drg. Melati Kusuma', email: 'melati@senyumsehat.id', plan: 'growth', status: 'active', cycle: 'monthly', seats: 7, city: 'Bandung', joinedAt: '2026-11-03', health: 74, paymentMethod: 'va' },
    { company: 'PT Mitra Tekstil Priangan', contact: 'Wawan Setiawan', email: 'wawan@mitratekstil.co.id', plan: 'enterprise', status: 'at-risk', cycle: 'monthly', seats: 140, city: 'Bandung', joinedAt: '2024-06-11', health: 38, paymentMethod: 'va' },
    { company: 'Apotek Sehati Farma', contact: 'Teguh Wibowo', email: 'teguh@sehatifarma.id', plan: 'growth', status: 'active', cycle: 'annual', seats: 5, city: 'Surakarta', joinedAt: '2025-10-28', health: 77, paymentMethod: 'ewallet' },
    { company: 'Sekolah Cendekia Global', contact: 'Fajar Nugroho', email: 'fajar@cendekiaglobal.sch.id', plan: 'scale', status: 'active', cycle: 'annual', seats: 45, city: 'Tangerang', joinedAt: '2024-07-22', health: 86, paymentMethod: 'transfer' },
    { company: 'PT Cahaya Energi Terbarukan', contact: 'Rizky Ramadhan', email: 'rizky@cahayaenergi.co.id', plan: 'scale', status: 'active', cycle: 'monthly', seats: 28, city: 'Balikpapan', joinedAt: '2025-01-09', health: 79, paymentMethod: 'card' }
]

const STATUS_TARGETS: Record<CustomerStatus, number> = {
    active: 2271,
    'at-risk': 147,
    trial: 236,
    canceled: 188
}

const FIRST_JOIN = '2023-02-01'

const PLAN_WEIGHTS: [PlanId, number][] = [
    ['starter', 46],
    ['growth', 32],
    ['scale', 17],
    ['enterprise', 5]
]

const SEAT_RANGES: Record<PlanId, [number, number]> = {
    starter: [1, 3],
    growth: [3, 14],
    scale: [12, 58],
    enterprise: [45, 320]
}

const HEALTH_RANGES: Record<CustomerStatus, [number, number]> = {
    active: [62, 98],
    'at-risk': [18, 46],
    trial: [35, 80],
    canceled: [4, 28]
}

const PAYMENT_WEIGHTS: [PaymentMethod, number][] = [
    ['va', 32],
    ['card', 24],
    ['qris', 18],
    ['transfer', 16],
    ['ewallet', 10]
]

const BUSINESS_TYPES: [string, number][] = [
    ['PT', 14],
    ['CV', 8],
    ['Toko', 8],
    ['Kafe', 5],
    ['Kedai', 4],
    ['Klinik', 5],
    ['Apotek', 4],
    ['Studio', 4],
    ['Butik', 4],
    ['Bengkel', 3],
    ['Salon', 3],
    ['Laundry', 3],
    ['Percetakan', 2],
    ['Koperasi', 3],
    ['Yayasan', 2],
    ['Sekolah', 2],
    ['Hotel', 3],
    ['Rumah Makan', 4],
    ['Galeri', 2],
    ['Katering', 3],
    ['Agensi', 3],
    ['Toko Online', 3]
]

const NAMES_A = [
    'Senja', 'Mentari', 'Kirana', 'Cahaya', 'Samudra', 'Bintang', 'Pelita', 'Lentera', 'Rinjani', 'Merapi',
    'Bahari', 'Permata', 'Mutiara', 'Kencana', 'Swarna', 'Nirwana', 'Rajawali', 'Melati', 'Cempaka', 'Anggrek',
    'Teratai', 'Kenari', 'Pandan', 'Bambu', 'Tunas', 'Arta', 'Dirga', 'Puri', 'Harmoni', 'Pesona',
    'Cendana', 'Kartika', 'Ananta', 'Banyu', 'Langit', 'Pertiwi', 'Selaras', 'Sinergi', 'Laksana', 'Surya',
    'Candra', 'Damar', 'Gading', 'Jati', 'Kemuning', 'Mahoni', 'Nawasena', 'Padi', 'Rimba', 'Sagara',
    'Tirta', 'Wangi', 'Zamrud', 'Lazuardi', 'Sekar'
]

const NAMES_B = [
    'Sentosa', 'Lestari', 'Mandiri', 'Sejahtera', 'Abadi', 'Makmur', 'Jaya', 'Prima', 'Utama', 'Mulia',
    'Bersama', 'Sukses', 'Berkah', 'Amanah', 'Indah', 'Raya', 'Asri', 'Perkasa', 'Gemilang', 'Persada',
    'Digital', 'Kreasi', 'Global', 'Niaga', 'Sentral', 'Semesta', 'Karya', 'Lancar', 'Harapan', 'Nusa'
]

const PT_SUFFIXES = ['Indonesia', 'Nusantara', 'Teknologi', 'Solusi', 'Internasional', 'Grup', 'Utama', 'Persada']

const FIRST_NAMES = [
    'Andi', 'Budi', 'Citra', 'Dewi', 'Eka', 'Fajar', 'Gita', 'Hendra', 'Indah', 'Joko',
    'Kartika', 'Lestari', 'Maya', 'Nanda', 'Putri', 'Rizky', 'Sari', 'Teguh', 'Utami', 'Vina',
    'Wahyu', 'Yuni', 'Agus', 'Bayu', 'Dimas', 'Fitri', 'Galih', 'Hana', 'Ilham', 'Intan',
    'Kevin', 'Laras', 'Mega', 'Nadia', 'Rani', 'Reza', 'Rina', 'Sinta', 'Taufik', 'Wulan',
    'Yoga', 'Arif', 'Bima', 'Dian', 'Eko', 'Farah', 'Gilang', 'Ika', 'Johan', 'Krisna',
    'Lina', 'Made', 'Nyoman', 'Ketut', 'Wayan', 'Ratna', 'Siti', 'Yusuf', 'Aditya', 'Novi'
]

const LAST_NAMES = [
    'Pratama', 'Wijaya', 'Saputra', 'Lestari', 'Santoso', 'Hidayat', 'Nugroho', 'Kusuma', 'Wibowo', 'Setiawan',
    'Gunawan', 'Rahman', 'Hakim', 'Siregar', 'Nasution', 'Harahap', 'Simanjuntak', 'Sinaga', 'Tanjung', 'Ramadhan',
    'Permana', 'Firmansyah', 'Hartono', 'Susanto', 'Halim', 'Tanuwijaya', 'Sutanto', 'Wirawan', 'Purnomo', 'Suryadi',
    'Anggraini', 'Maharani', 'Handayani', 'Kurniawan', 'Utomo', 'Prasetyo', 'Rachmawati', 'Salim', 'Hasibuan', 'Manurung',
    'Tambunan', 'Sembiring', 'Ginting', 'Tarigan', 'Wenas', 'Mahendra', 'Pradana', 'Syahputra', 'Daulay', 'Lubis'
]

const CITY_WEIGHTS = CITIES.map((item): [typeof item, number] => [item, item.weight])

const slug = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]/g, '')

const buildCompany = (random: Random): { company: string, base: string, tld: string } => {
    const type = random.weighted(BUSINESS_TYPES)
    const first = random.pick(NAMES_A)
    const second = random.pick(NAMES_B)

    if (type === 'PT') {
        const picked = random.pick(PT_SUFFIXES)
        const suffix = picked === second ? 'Indonesia' : picked

        return { company: `PT ${first} ${second} ${suffix}`, base: slug(`${first}${second}`), tld: 'co.id' }
    }

    if (type === 'CV') {
        return { company: `CV ${first} ${second}`, base: slug(`${first}${second}`), tld: random.pick(['co.id', 'id']) }
    }

    const words = random.chance(0.55) ? [type, first] : [type, first, second]
    const tld = random.weighted<string>([['id', 5], ['com', 3], ['co.id', 1], ['web.id', 1]])

    return { company: words.join(' '), base: slug(words.join('')), tld }
}

const createCompany = (random: Random, usedNames: Set<string>, city: string): { company: string, domain: string } => {
    for (let attempt = 0; attempt < 12; attempt++) {
        const candidate = buildCompany(random)

        if (!usedNames.has(candidate.company)) {
            usedNames.add(candidate.company)
            return { company: candidate.company, domain: `${candidate.base}.${candidate.tld}` }
        }
    }

    const fallback = buildCompany(random)
    const company = `${fallback.company} ${city}`
    usedNames.add(company)

    return { company, domain: `${fallback.base}${slug(city)}.${fallback.tld}` }
}

const generateSeed = (random: Random, status: CustomerStatus, usedNames: Set<string>): CustomerSeed => {
    const plan = random.weighted(PLAN_WEIGHTS)
    const cycle: BillingCycle = random.chance(0.34) ? 'annual' : 'monthly'
    const location = random.weighted(CITY_WEIGHTS)
    const { company, domain } = createCompany(random, usedNames, location.city)
    const firstName = random.pick(FIRST_NAMES)
    const lastName = random.pick(LAST_NAMES)
    const localPart = random.weighted<string>([
        [slug(firstName), 5],
        [`${slug(firstName)}.${slug(lastName)}`, 3],
        [`${slug(firstName).charAt(0)}${slug(lastName)}`, 2]
    ])
    const [minSeats, maxSeats] = SEAT_RANGES[plan]
    const [minHealth, maxHealth] = HEALTH_RANGES[status]

    let joinedAt: string

    if (status === 'trial') {
        joinedAt = addDays(TODAY, -random.int(0, 13))
    }
    else {
        const latest = addDays(TODAY, status === 'canceled' ? -45 : -15)
        const span = daysBetween(FIRST_JOIN, latest)
        joinedAt = addDays(FIRST_JOIN, Math.floor(span * Math.sqrt(random.next())))
    }

    const canceledAt = status === 'canceled'
        ? addDays(joinedAt, random.int(30, Math.max(30, daysBetween(joinedAt, TODAY))))
        : undefined

    return {
        company,
        contact: `${firstName} ${lastName}`,
        email: `${localPart}@${domain}`,
        plan,
        status,
        cycle,
        seats: random.int(minSeats, maxSeats),
        city: location.city,
        joinedAt,
        canceledAt,
        health: random.int(minHealth, maxHealth),
        paymentMethod: random.weighted(PAYMENT_WEIGHTS)
    }
}

const finalize = (seed: CustomerSeed, index: number): Customer => ({
    id: `CUS-${String(index + 1).padStart(4, '0')}`,
    company: seed.company,
    contact: seed.contact,
    email: seed.email,
    plan: seed.plan,
    status: seed.status,
    cycle: seed.cycle,
    mrr: computeMrr(seed.plan, seed.cycle, seed.seats),
    seats: seed.seats,
    city: seed.city,
    province: getProvince(seed.city),
    joinedAt: seed.joinedAt,
    canceledAt: seed.status === 'canceled' ? (seed.canceledAt ?? TODAY) : null,
    trialEndsAt: seed.status === 'trial' ? addDays(seed.joinedAt, 14) : null,
    health: seed.health,
    paymentMethod: seed.paymentMethod
})

const createCustomers = (): Customer[] => {
    const random = createRandom(20231108)
    const usedNames = new Set(FEATURED.map((seed) => seed.company))
    const remaining: Record<CustomerStatus, number> = { ...STATUS_TARGETS }

    FEATURED.forEach((seed) => {
        remaining[seed.status] -= 1
    })

    const pool: CustomerStatus[] = []
    const statuses = Object.keys(remaining) as CustomerStatus[]

    statuses.forEach((status) => {
        for (let index = 0; index < remaining[status]; index++) pool.push(status)
    })

    const customers = FEATURED.map((seed, index) => finalize(seed, index))

    random.shuffle(pool).forEach((status) => {
        customers.push(finalize(generateSeed(random, status, usedNames), customers.length))
    })

    return customers
}

export const CUSTOMERS: Customer[] = createCustomers()

export const isPaying = (customer: Pick<Customer, 'status'>): boolean => customer.status === 'active' || customer.status === 'at-risk'

export const getCustomerStats = (list: Customer[]) => {
    const paying = list.filter(isPaying)
    const mrr = paying.reduce((sum, customer) => sum + customer.mrr, 0)

    return {
        total: list.length,
        active: list.filter((customer) => customer.status === 'active').length,
        atRisk: list.filter((customer) => customer.status === 'at-risk').length,
        trial: list.filter((customer) => customer.status === 'trial').length,
        canceled: list.filter((customer) => customer.status === 'canceled').length,
        paying: paying.length,
        mrr,
        arpu: paying.length > 0 ? mrr / paying.length : 0
    }
}

export const CUSTOMER_STATS = getCustomerStats(CUSTOMERS)

export const PLAN_BREAKDOWN = PLANS.map((plan) => {
    const paying = CUSTOMERS.filter((customer) => isPaying(customer) && customer.plan === plan.id)

    return {
        plan: plan.id,
        customers: paying.length,
        mrr: paying.reduce((sum, customer) => sum + customer.mrr, 0)
    }
})

export const PROVINCE_BREAKDOWN = (() => {
    const counts = new Map<string, number>()

    CUSTOMERS.filter(isPaying).forEach((customer) => {
        counts.set(customer.province, (counts.get(customer.province) ?? 0) + 1)
    })

    return Array.from(counts.entries())
        .map(([province, customers]) => ({ province, customers }))
        .sort((a, b) => b.customers - a.customers)
})()

const CUSTOMER_BY_ID = new Map(CUSTOMERS.map((customer) => [customer.id, customer]))

export const getCustomerById = (id: string): Customer | undefined => CUSTOMER_BY_ID.get(id)

export const FEATURED_CUSTOMER_IDS = {
    kopiSenja: 'CUS-0001',
    majuBersama: 'CUS-0005',
    warungTani: 'CUS-0007',
    kafeLangitBiru: 'CUS-0009',
    klinikGigi: 'CUS-0010',
    mitraTekstil: 'CUS-0011'
}
